import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Stack,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CloudUpload,
  InsertDriveFile,
  DownloadOutlined,
  ErrorOutline,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';

const fmtSize = (b: number) => {
  if (!b) return '';
  const k = 1024, s = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return parseFloat((b / Math.pow(k, i)).toFixed(1)) + ' ' + s[i];
};

const Download: React.FC = () => {
  const { fileId, filename } = useParams<{ fileId: string; filename: string }>();
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState('');
  const [info, setInfo] = useState<{ filename: string; size: number; type: string } | null>(null);
  const theme = useTheme();

  useEffect(() => {
    if (!fileId) { setStatus('error'); setError('Invalid link'); return; }
    const base = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3100';
    fetch(`${base}/download/generate-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileId }),
    })
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json()).error || 'File not found');
        const d = await r.json();
        setInfo({ filename: d.filename || decodeURIComponent(filename || ''), size: d.size || 0, type: d.type || '' });
        setStatus('ready');
      })
      .catch((e) => { setError(e.message); setStatus('error'); });
  }, [fileId, filename]);

  const handleDownload = () => {
    const base = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3100';
    window.location.href = `${base}/download/${fileId}/${encodeURIComponent(info?.filename || filename || 'file')}`;
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default', p: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          maxWidth: 440,
          width: '100%',
          borderRadius: 4,
          border: `1px solid ${theme.palette.divider}`,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: 'linear-gradient(90deg, #7C6FFF, #FF6B8A)',
          },
        }}
      >
        {/* Brand */}
        <Stack alignItems="center" spacing={0.5} sx={{ mb: 4, mt: 1 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              background: 'linear-gradient(135deg, #7C6FFF, #9D7BFF)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 6px 20px rgba(124,111,255,0.25)',
            }}
          >
            <CloudUpload sx={{ fontSize: 24, color: '#fff' }} />
          </Box>
          <Typography
            variant="h6"
            fontWeight={800}
            sx={{
              background: 'linear-gradient(135deg, #7C6FFF, #FF6B8A)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            UploadMe
          </Typography>
        </Stack>

        {status === 'loading' && (
          <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
            <CircularProgress size={28} />
            <Typography color="text.secondary" variant="body2">Loading file...</Typography>
          </Stack>
        )}

        {status === 'error' && (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 4 }}>
            <ErrorOutline sx={{ fontSize: 48, color: 'error.main' }} />
            <Typography color="error.main" fontWeight={600}>{error}</Typography>
            <Typography variant="body2" color="text.secondary">The file may have expired or been deleted.</Typography>
          </Stack>
        )}

        {status === 'ready' && info && (
          <Stack alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <InsertDriveFile sx={{ fontSize: 32, color: 'primary.main' }} />
            </Box>
            <Typography variant="h6" fontWeight={600} sx={{ wordBreak: 'break-word', fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
              {info.filename}
            </Typography>
            <Stack direction="row" spacing={0.75}>
              {info.size > 0 && <Chip label={fmtSize(info.size)} size="small" variant="outlined" />}
              {info.type && <Chip label={info.type.split('/').pop()?.toUpperCase()} size="small" variant="outlined" />}
            </Stack>
            <Button
              variant="contained"
              size="large"
              startIcon={<DownloadOutlined />}
              onClick={handleDownload}
              sx={{ mt: 1, px: 5 }}
            >
              Download
            </Button>
          </Stack>
        )}

        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 4 }}>
          Shared via uploadme.sh
        </Typography>
      </Paper>
    </Box>
  );
};

export default Download;
